import type { Folder, Phrase } from './types';

const LIBRARY_CACHE = 'dp-library-v2';
const MANIFEST_URL = '/library/manifest.json';

type LibraryManifest = {
  version: string;
  count: number;
  corpusUrl: string;
  searchIndexUrl: string;
};

export type ClientLibrary = {
  phrases: Phrase[];
  folders: Folder[];
  searchIndexUrl: string;
  version: string;
};

async function openCache(): Promise<Cache | undefined> {
  if (!('caches' in window)) return undefined;
  try {
    return await caches.open(LIBRARY_CACHE);
  } catch (_) {
    return undefined;
  }
}

async function networkFirst(request: Request, cache?: Cache): Promise<Response> {
  try {
    const response = await fetch(request);
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    await cache?.put(request, response.clone());
    return response;
  } catch (error) {
    const cached = await cache?.match(request);
    if (cached) return cached;
    throw error;
  }
}

async function cacheFirst(url: string, cache?: Cache): Promise<Response> {
  const request = new Request(url);
  const cached = await cache?.match(request);
  if (cached) return cached;
  const response = await fetch(request);
  if (!response.ok) throw new Error(`Request failed (${response.status})`);
  await cache?.put(request, response.clone());
  return response;
}

function isManifest(value: unknown): value is LibraryManifest {
  const manifest = value as Partial<LibraryManifest> | null;
  return Boolean(
    manifest && typeof manifest.version === 'string' && typeof manifest.count === 'number' &&
    typeof manifest.corpusUrl === 'string' && typeof manifest.searchIndexUrl === 'string'
  );
}

/** Load the complete, build-parsed library and retain it for offline launches. */
export async function loadClientLibrary(): Promise<ClientLibrary> {
  const cache = await openCache();
  const manifestRequest = new Request(MANIFEST_URL, { cache: 'no-cache' });
  const manifestResponse = await networkFirst(manifestRequest, cache);
  const manifest: unknown = await manifestResponse.json();
  if (!isManifest(manifest)) throw new Error('Library manifest is invalid');

  const corpusResponse = await cacheFirst(manifest.corpusUrl, cache);
  const corpus = await corpusResponse.json() as { phrases?: Phrase[]; folders?: Folder[] };
  if (!Array.isArray(corpus.phrases) || !Array.isArray(corpus.folders)) {
    throw new Error('Library corpus is invalid');
  }
  if (corpus.phrases.length !== manifest.count) {
    throw new Error(`Library is incomplete (${corpus.phrases.length}/${manifest.count} notes)`);
  }

  return {
    phrases: corpus.phrases,
    folders: corpus.folders,
    searchIndexUrl: manifest.searchIndexUrl,
    version: manifest.version,
  };
}
